import {
  affiliateRefFromSearch,
  mobileInstallApiUrl,
} from "../utils/affiliateInstallRedirect";
import { getLogfrogVisitorId } from "../utils/logfrog";
import { FIRST_TOUCH_UTM_KEYS, getFirstTouch } from "./FirstTouchService";

const STORAGE_KEY = "dealhawk_referral_id";
const ACQUISITION_DEAL_KEY = "dealhawk_acquisition_deal_id";
const SIGNUP_REFERRER_DOMAIN_KEY = "dealhawk_signup_referrer_domain";

type ToltWindow = Window & { tolt_referral?: string };

export function persistReferral(code: string) {
  const trimmed = code.trim();
  if (!trimmed) return;
  localStorage.setItem(STORAGE_KEY, trimmed);
}

export function captureReferralFromURL(search = window.location.search) {
  const fromUrl = affiliateRefFromSearch(search);
  if (fromUrl) persistReferral(fromUrl);
}

export function captureToltReferral() {
  const ref = (window as ToltWindow).tolt_referral?.trim();
  if (ref) persistReferral(ref);
}

export function getReferralForSignup(): string | undefined {
  captureToltReferral();
  return localStorage.getItem(STORAGE_KEY)?.trim() || undefined;
}

export async function waitForReferral(timeoutMs = 1500): Promise<string | undefined> {
  const deadline = Date.now() + timeoutMs;
  let ref = getReferralForSignup();
  while (!ref && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 200));
    captureReferralFromURL();
    captureToltReferral();
    ref = getReferralForSignup();
  }
  return ref;
}

export function captureAcquisitionDeal(search = window.location.search) {
  const dealId = new URLSearchParams(search).get("deal_ref")?.trim();
  if (dealId) localStorage.setItem(ACQUISITION_DEAL_KEY, dealId);
}

function normalizeDomain(hostname: string): string | undefined {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (!normalized) return undefined;
  return normalized.startsWith("www.") ? normalized.slice(4) : normalized;
}

export function captureSignupReferrerFromURL(
  search = window.location.search,
) {
  if (localStorage.getItem(SIGNUP_REFERRER_DOMAIN_KEY)) return;
  const value = new URLSearchParams(search).get("referrer_domain")?.trim();
  if (!value) return;
  try {
    const url = new URL(value.includes("://") ? value : `https://${value}`);
    const domain = normalizeDomain(url.hostname);
    if (domain) localStorage.setItem(SIGNUP_REFERRER_DOMAIN_KEY, domain);
  } catch {
    // Invalid campaign attribution must never interrupt the landing page.
  }
}

export function captureSignupReferrer(
  referrer = document.referrer,
  currentHostname = window.location.hostname,
) {
  if (localStorage.getItem(SIGNUP_REFERRER_DOMAIN_KEY) || !referrer) return;

  // OAuth providers become document.referrer on the callback page. They are
  // part of authentication, not the acquisition source that brought the user
  // to DealHawk in the first place.
  if (window.location.pathname.startsWith("/auth/")) return;

  try {
    const referrerDomain = normalizeDomain(new URL(referrer).hostname);
    const currentDomain = normalizeDomain(currentHostname);
    if (!referrerDomain || referrerDomain === currentDomain) return;
    localStorage.setItem(SIGNUP_REFERRER_DOMAIN_KEY, referrerDomain);
  } catch {
    // Browsers normally provide an absolute document.referrer. Ignore an
    // invalid value rather than letting attribution interrupt signup.
  }
}

export function getSignupReferrerDomain(): string | undefined {
  captureSignupReferrerFromURL();
  captureSignupReferrer();
  return localStorage.getItem(SIGNUP_REFERRER_DOMAIN_KEY)?.trim() || undefined;
}

export function clearSignupReferrer() {
  localStorage.removeItem(SIGNUP_REFERRER_DOMAIN_KEY);
}

export type InstallPlatform = "ios" | "android";

/**
 * Attribution the /install endpoint stores on its install token so the app can
 * later report whether this person saw the website before installing:
 * LogFrog web visitor id, first-touch UTMs, landing path and first-seen time,
 * plus the affiliate ref and referring domain.
 */
export function getInstallAttributionParams(): Record<string, string> {
  const params: Record<string, string> = {};
  const safe = <T,>(read: () => T): T | undefined => {
    try {
      return read();
    } catch {
      return undefined;
    }
  };
  const referralId = safe(getReferralForSignup);
  if (referralId) params.ref = referralId;
  const signupReferrerDomain = safe(getSignupReferrerDomain);
  const firstTouch = safe(getFirstTouch);
  const referrerDomain = signupReferrerDomain || firstTouch?.referrerDomain;
  if (referrerDomain) params.referrer_domain = referrerDomain;
  const visitorId = safe(() => getLogfrogVisitorId());
  if (visitorId) params.lf_vid = visitorId;
  if (firstTouch) {
    for (const key of FIRST_TOUCH_UTM_KEYS) {
      const value = firstTouch[key];
      if (value) params[key] = value;
    }
    if (firstTouch.landingPath) params.landing_path = firstTouch.landingPath;
    if (firstTouch.firstSeenAt) params.first_seen = String(firstTouch.firstSeenAt);
  }
  return params;
}

/** Single builder for every App Store / Google Play badge link on the web. */
export function buildInstallUrl(platform: InstallPlatform): string {
  const params = new URLSearchParams({ platform });
  for (const [key, value] of Object.entries(getInstallAttributionParams())) {
    params.set(key, value);
  }
  return `${mobileInstallApiUrl()}/install?${params.toString()}`;
}

/** @deprecated Use buildInstallUrl. */
export const getMobileInstallUrl = buildInstallUrl;

export function getAcquisitionDealForSignup(): string | undefined {
  captureAcquisitionDeal();
  return localStorage.getItem(ACQUISITION_DEAL_KEY)?.trim() || undefined;
}

export function clearAcquisitionDealAfterSignup() {
  localStorage.removeItem(ACQUISITION_DEAL_KEY);
}

export function clearReferralAfterClaim() {
  localStorage.removeItem(STORAGE_KEY);
}

export function initReferralCapture() {
  captureReferralFromURL();
  captureAcquisitionDeal();
  captureSignupReferrerFromURL();
  captureSignupReferrer();
  captureToltReferral();
  const interval = window.setInterval(captureToltReferral, 500);
  window.setTimeout(() => window.clearInterval(interval), 5000);
  return () => window.clearInterval(interval);
}
